import type { FetchRequestError } from "@lib/errors";
import { FetchNotFoundError, FetchUnauthorizedError, FetchPasswordInvalidError, FetchSsoError } from "@lib/errors";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import { getBrazeApiDenialCode } from "./getBrazeApiDenialCode";

const doNothing = () => {
  // no action
};

/**
 * Given a FetchRequestError, return a function that should be executed when the user clicks "OK" on the error modal
 */
export const getFetchErrorModalAction = (error: FetchRequestError): (() => void) => {
  const denialCode = getBrazeApiDenialCode(error);
  // Braze::API denials carry a machine-readable code that can pick a better recovery than the
  // error class alone. A stale CSRF token and the sandbox-ToS gate are both cured by a reload
  // (the reload mints a fresh token / routes to the ToS acknowledgement); an IP-allowlist or
  // company-ban denial is cured by nothing the user can click, so OK just dismisses.
  switch (denialCode) {
    case "forbidden.csrf":
    case "sandbox_tos.required":
      return () => {
        window.location.reload();
      };
    case "unauthorized.ip_not_allowed":
    case "unauthorized.company_banned":
      return doNothing;
  }
  if (error instanceof FetchNotFoundError) {
    return () => {
      window.history.back();
    };
  }
  if (error instanceof FetchUnauthorizedError) {
    return () => {
      window.history.back();
    };
  }
  if (error instanceof FetchPasswordInvalidError) {
    return () => {
      window.location.reload();
    };
  }
  // A reload fixes the 409 that ApplicationController#render_409 sends for a missing app group. Many legacy
  // endpoints also send a JSON 409, so every 409 without the Braze::API envelope keeps the reload. A Braze::API 409
  // carries a domain error_code that a reload cannot fix, so closing the modal does nothing, like an enveloped 400
  // or 422.
  if (error.status === UNIDENTIFIED_ERROR && denialCode === undefined) {
    return () => {
      window.location.reload();
    };
  }
  if (error instanceof FetchSsoError) {
    return () => {
      window.location.reload();
    };
  }
  return doNothing;
};
